import React, { useEffect, useMemo, useState } from 'react';
import {
  pageContainer, headerStyle, breadcrumb, titleStyle, subtitleStyle,
  cardStyle, sectionTitle, primaryBtn, secondaryBtn, thStyle, tdStyle, badgeStyle,
  inputStyle, labelStyle,
} from '../styles/theme';
import API from '../services/api';
import {
  getAdmins,
  getAdminHierarchy,
  getAdminPerformanceReport,
  getDisputes,
  getSupportTickets,
  AdminListItem,
  HierarchyNode,
  AdminPerformanceItem,
} from '../services/adminService';
import { isSuperAdmin } from '../utils/currentUser';

const TreeNode: React.FC<{ node: HierarchyNode; depth?: number }> = ({ node, depth = 0 }) => (
  <div style={{ marginLeft: depth * 22, paddingLeft: 10, borderLeft: depth ? '2px solid #e2e8f0' : 'none' }}>
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, margin: '6px 0' }}>
      <span style={{ ...badgeStyle, background: node.role === 'SUPER_ADMIN' ? '#fef3c7' : node.role === 'ADMIN' ? '#eff6ff' : '#f0fdf4', color: node.role === 'SUPER_ADMIN' ? '#d97706' : node.role === 'ADMIN' ? '#2563eb' : '#16a34a' }}>{node.role}</span>
      <span style={{ fontWeight: 600 }}>{node.name}</span>
    </div>
    {(node.children || []).map((child) => <TreeNode key={child.adminId} node={child} depth={depth + 1} />)}
  </div>
);

const Admins = () => {
  const [admins, setAdmins] = useState<AdminListItem[]>([]);
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [hierarchyMappings, setHierarchyMappings] = useState<any[]>([]);
  const [performance, setPerformance] = useState<AdminPerformanceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '', religion: 'ISLAM', role: 'ADMIN', department: '', reportsTo: '' });

  const ALL_ADMIN_ROLES = [
    { value: 'SUPER_ADMIN', label: 'Super Admin' },
    { value: 'ADMIN',       label: 'Admin' },
    { value: 'SUPPORT',     label: 'Support' },
    { value: 'FINANCE',     label: 'Finance' },
    { value: 'AD_MANAGER',  label: 'Ad Manager' },
    { value: 'OPERATIONS',  label: 'Operations' },
  ];

  const ROLE_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
    SUPER_ADMIN: { bg: '#fef3c7', color: '#d97706' },
    ADMIN:       { bg: '#eff6ff', color: '#2563eb' },
    SUPPORT:     { bg: '#f0fdf4', color: '#16a34a' },
    FINANCE:     { bg: '#fff7ed', color: '#ea580c' },
    AD_MANAGER:  { bg: '#fdf4ff', color: '#9333ea' },
    OPERATIONS:  { bg: '#f0f9ff', color: '#0284c7' },
  };

  const getRoleBadge = (role: string) => ROLE_BADGE_COLORS[role] ?? { bg: '#f1f5f9', color: '#64748b' };
  const getRoleLabel = (role: string) => ALL_ADMIN_ROLES.find(r => r.value === role)?.label ?? role;

  const isSA = isSuperAdmin();

  const fetchAdminsData = async () => {
    setLoading(true);
    try {
      const [adminsData, hierarchyData, perfData, ticketsData, disputesData] = await Promise.all([
        getAdmins(),
        getAdminHierarchy(),
        getAdminPerformanceReport(),
        getSupportTickets(),
        getDisputes(),
      ]);

      setAdmins(adminsData || []);
      setHierarchy(hierarchyData?.tree || []);
      setHierarchyMappings(hierarchyData?.mappings || []);

      if (perfData?.length) {
        setPerformance(perfData);
      } else {
        const localComputed = (adminsData || []).map((admin) => ({
          adminId: admin.id,
          name: admin.name,
          role: admin.role,
          ticketsSolved: (ticketsData.items || []).filter((t: any) => 
            (t.status === 'RESOLVED' || t.status === 'CLOSED') && 
            (t.resolvedBy === admin.id || 
             (t.resolvedBy || '').toLowerCase() === admin.name.toLowerCase() ||
             (t.assignedToAdminId === admin.id && (!t.resolvedBy || ['admin', 'system'].includes(t.resolvedBy.toLowerCase()))))
          ).length,
          disputesSolved: (disputesData.items || []).filter((d: any) => 
            d.disputeResolvedBy === admin.id || (d.disputeResolvedBy || '').toLowerCase() === admin.name.toLowerCase()
          ).length,
        }));
        setPerformance(localComputed);
      }
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load user insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdminsData(); }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await API.post('/admin/create', newAdmin);
      setIsModalOpen(false);
      setNewAdmin({ name: '', email: '', password: '', religion: 'ISLAM', role: 'ADMIN', department: '', reportsTo: '' });
      fetchAdminsData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete user "${name}"?`)) return;
    try {
      await API.delete(`/admin/admins/${id}`);
      fetchAdminsData();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const topPerformer = useMemo(() => [...performance].sort((a, b) => b.ticketsSolved - a.ticketsSolved)[0], [performance]);

  return (
    <div style={pageContainer}>
      <header style={headerStyle}>
        <div>
          <div style={breadcrumb}>System / Users</div>
          <h1 style={titleStyle}>System Users Control Panel</h1>
          <p style={subtitleStyle}>Manage system user accounts, roles, hierarchy and operational performance.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={secondaryBtn} onClick={fetchAdminsData} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
          {isSA && <button style={primaryBtn} onClick={() => setIsModalOpen(true)}>+ Add User</button>}
        </div>
      </header>

      {!isSA && (
        <div style={{ padding: 14, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', borderRadius: 10, marginBottom: 20 }}>
          This page includes super-admin features. You currently have read-only access.
        </div>
      )}

      {error ? <div style={{ padding: '20px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', border: '1px solid #fca5a5' }}>{error}</div> : (
        <>
          {topPerformer && (
            <div style={{ ...cardStyle, marginBottom: 24 }}>
              <h3 style={{ ...sectionTitle, marginBottom: 8 }}>Top Current Performer</h3>
              <div style={{ fontSize: 14 }}><strong>{topPerformer.name}</strong> solved {topPerformer.ticketsSolved} tickets.</div>
            </div>
          )}

          <div style={{ ...cardStyle, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ ...sectionTitle, margin: 0 }}>System Users</h3>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <th style={thStyle}>NAME</th><th style={thStyle}>EMAIL</th><th style={thStyle}>ROLE</th><th style={thStyle}>DEPARTMENT</th><th style={thStyle}>RELIGION</th><th style={thStyle}>CREATED</th><th style={{ ...thStyle, textAlign: 'right' }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading users…</td></tr> : admins.length === 0 ? <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No users found.</td></tr> : admins.map(admin => (
                    <tr key={admin.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={tdStyle}><div style={{ fontWeight: '600', color: '#1e293b' }}>{admin.name}</div></td>
                      <td style={tdStyle}>{admin.email}</td>
                      <td style={tdStyle}><span style={{ ...badgeStyle, ...getRoleBadge(admin.role) }}>{getRoleLabel(admin.role)}</span></td>
                      <td style={tdStyle}>{admin.department || 'GLOBAL'}</td>
                      <td style={tdStyle}>{admin.religion || '—'}</td>
                      <td style={tdStyle}>{new Date(admin.createdAt).toLocaleDateString()}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{isSA && <button onClick={() => handleDelete(admin.id, admin.name)} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca', background: '#fef2f2' }}>Delete</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div style={cardStyle}>
              <h3 style={sectionTitle}>Organizational Hierarchy Mapping</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <th style={thStyle}>USER</th><th style={thStyle}>REPORTS TO (SUPER ADMIN)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hierarchyMappings.length === 0 ? <tr><td colSpan={2} style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>No mapping data.</td></tr> : hierarchyMappings.map((h, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f8fafc' }}>
                        <td style={tdStyle}>{h.adminName}</td><td style={tdStyle}>{h.superAdminName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={cardStyle}>
              <h3 style={sectionTitle}>Visual Tree View</h3>
              {hierarchy.length ? hierarchy.map((node) => <TreeNode key={node.adminId} node={node} />) : <p style={{ color: '#64748b', margin: 0 }}>No hierarchy API data found.</p>}
            </div>

            <div style={cardStyle}>
              <h3 style={sectionTitle}>User-wise Ticket Report</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <th style={thStyle}>USER</th><th style={thStyle}>ROLE</th><th style={thStyle}>TICKETS</th>
                  </tr>
                </thead>
                <tbody>
                  {performance.length === 0 ? <tr><td colSpan={3} style={{ ...tdStyle, textAlign: 'center', color: '#64748b' }}>No performance records.</td></tr> : performance.map((item) => (
                    <tr key={item.adminId} style={{ borderBottom: '1px solid #f8fafc' }}>
                      <td style={tdStyle}>{item.name}</td><td style={tdStyle}>{item.role}</td><td style={tdStyle}>{item.ticketsSolved}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {isModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ ...cardStyle, width: '100%', maxWidth: '480px', margin: '20px' }}>
            <h3 style={sectionTitle}>Create New User</h3>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
                <div><label style={labelStyle}>Full Name</label><input style={inputStyle} required value={newAdmin.name} onChange={e => setNewAdmin({ ...newAdmin, name: e.target.value })} placeholder='John Doe' /></div>
                <div><label style={labelStyle}>Email Address</label><input style={inputStyle} type='email' required value={newAdmin.email} onChange={e => setNewAdmin({ ...newAdmin, email: e.target.value })} placeholder='user@divinifyy.com' /></div>
                <div><label style={labelStyle}>Password</label><input style={inputStyle} type='password' required minLength={8} value={newAdmin.password} onChange={e => setNewAdmin({ ...newAdmin, password: e.target.value })} placeholder='••••••••' /></div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Role</label>
                    <select style={inputStyle} value={newAdmin.role} onChange={e => setNewAdmin({ ...newAdmin, role: e.target.value, reportsTo: '' })}>
                      {ALL_ADMIN_ROLES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Department</label>
                    <select style={inputStyle} value={newAdmin.department} onChange={e => setNewAdmin({ ...newAdmin, department: e.target.value })}>
                      <option value="">None (Global)</option>
                      <option value="SUPPORT">Support</option>
                      <option value="FINANCE">Finance</option>
                      <option value="OPERATIONS">Operations</option>
                      <option value="MARKETING">Marketing</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Religion</label>
                    <select style={inputStyle} value={newAdmin.religion} onChange={e => setNewAdmin({ ...newAdmin, religion: e.target.value })}>
                      <option value='ISLAM'>Islam</option>
                      <option value='HINDUISM'>Hinduism</option>
                      <option value='CHRISTIANITY'>Christianity</option>
                    </select>
                  </div>
                </div>
                {newAdmin.role !== 'SUPER_ADMIN' && (
                  <div>
                    <label style={labelStyle}>Reports To (Super Admin)</label>
                    <select style={inputStyle} value={newAdmin.reportsTo} onChange={e => setNewAdmin({ ...newAdmin, reportsTo: e.target.value })}>
                      <option value="">Select Super Admin</option>
                      {admins.filter(a => a.role === 'SUPER_ADMIN').map(sa => (
                        <option key={sa.id} value={sa.id}>{sa.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type='button' style={secondaryBtn} onClick={() => setIsModalOpen(false)}>Cancel</button>
                <button type='submit' style={primaryBtn} disabled={creating}>{creating ? 'Creating...' : 'Create Account'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admins;
